import React, { useState, useEffect } from 'react';
import Icon from '../../components/AppIcon';
import BlogCard from './components/BlogCard';
import FeaturedCarousel from './components/FeaturedCarousel';
import Sidebar from './components/Sidebar';
import SearchFilters from './components/SearchFilters';
import Footer from 'pages/home/components/Footer';
import BlogPostDetail from './components/BlogPostDetail';
import { getAllBlogs } from './components/blogapi';
import { Helmet } from 'react-helmet-async'

const POSTS_PER_PAGE = 6;

const Blog = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedAuthor, setSelectedAuthor] = useState('all');
  const [sortBy, setSortBy] = useState('latest');
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [allBlogPosts, setAllBlogPosts] = useState([]);
  const [filteredPosts, setFilteredPosts] = useState([]);
  const [selectedPostId, setSelectedPostId] = useState(null);
  const [totalPages, setTotalPages] = useState(1);

  const fetchBlogPosts = async () => {
    setIsLoading(true);
    setCurrentPage(1);
    try {
      const posts = await getAllBlogs();
      setAllBlogPosts(posts);
      setFilteredPosts(posts);
    } catch {
      setAllBlogPosts([]);
      setFilteredPosts([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchBlogPosts();
  }, []);

  useEffect(() => {
    let filtered = [...allBlogPosts];

    if (searchQuery) {
      filtered = filtered.filter(post =>
        post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        post.excerpt.toLowerCase().includes(searchQuery.toLowerCase()) ||
        post.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }

    if (selectedCategory !== 'all') {
      filtered = filtered.filter(post => post.category === selectedCategory);
    }

    if (selectedAuthor !== 'all') {
      filtered = filtered.filter(post => post.author.name === selectedAuthor);
    }

    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'latest': return new Date(b.publishedAt) - new Date(a.publishedAt);
        case 'oldest': return new Date(a.publishedAt) - new Date(b.publishedAt);
        case 'popular': return b.views - a.views;
        case 'comments': return b.comments - a.comments;
        default: return 0;
      }
    });

    setFilteredPosts(filtered);
    setCurrentPage(1);
    setTotalPages(Math.ceil(filtered.length / POSTS_PER_PAGE));
  }, [searchQuery, selectedCategory, selectedAuthor, sortBy, allBlogPosts]);

  const categories = allBlogPosts.reduce((acc, post) => {
    const categoryName = post.category;
    const existing = acc.find(c => c.id === categoryName);
    if (existing) existing.count += 1;
    else acc.push({ id: categoryName, name: categoryName, count: 1 });
    return acc;
  }, [{ id: 'all', name: 'All Categories', count: allBlogPosts.length }]);

  const authors = allBlogPosts.reduce((acc, post) => {
    const name = post.author.name;
    if (!acc.find(a => a.id === name)) acc.push({ id: name, name });
    return acc;
  }, [{ id: 'all', name: 'All Authors' }]);

  const featuredPosts = allBlogPosts.filter(post => post.featured);
  const startIndex = (currentPage - 1) * POSTS_PER_PAGE;
  const currentPosts = filteredPosts.slice(startIndex, startIndex + POSTS_PER_PAGE);
  const selectedPost = allBlogPosts.find(post => post.id === selectedPostId);

  const metaData = selectedPostId && selectedPost ? {
    title: selectedPost.metaTitle || selectedPost.title,
    description: selectedPost.metaDescription || selectedPost.subTitle || "Read our latest tech insights."
  } : {
    title: "Zentirx Tech Blog | Insights & Tutorials",
    description: "Insights, tutorials, and industry perspectives from Zentirx Infotech."
  };

  if (selectedPostId && selectedPost) {
    return (
      <div className="min-h-screen bg-background pt-12">
        <Helmet>
          <title>{metaData.title}</title>
          <meta name="description" content={metaData.description} />
        </Helmet>
        <BlogPostDetail post={selectedPost} handleBackToBlog={() => setSelectedPostId(null)} />
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pt-12">
      <Helmet>
        <title>{metaData.title}</title>
        <meta name="description" content={metaData.description} />
      </Helmet>
      <section className="bg-gradient-to-br from-primary-50 to-accent-50 py-16">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-heading font-bold text-text-primary mb-6">Zentirx Tech Blog</h1>
          <p className="text-xl text-text-secondary max-w-3xl mx-auto mb-8">Insights, tutorials, and industry perspectives.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <div className="relative w-full sm:w-96">
              <input
                type="text"
                placeholder="Search articles, topics, or authors..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border border-border rounded-lg focus:ring-primary text-sm"
              />
              <Icon name="Search" size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-text-secondary" />
            </div>
            <span className="text-sm text-text-secondary">{filteredPosts.length} articles found</span>
          </div>
        </div>
      </section>

      {featuredPosts.length > 0 && (
        <section className="py-4 bg-surface">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-heading font-semibold">Featured Articles</h2>
            </div>
            <FeaturedCarousel posts={featuredPosts} handleReadMore={setSelectedPostId} />
          </div>
        </section>
      )}

      <section className="py-8">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-8 px-4">
          <div className="lg:col-span-8">
            <SearchFilters
              categories={categories}
              authors={authors}
              selectedCategory={selectedCategory}
              selectedAuthor={selectedAuthor}
              sortBy={sortBy}
              onCategoryChange={setSelectedCategory}
              onAuthorChange={setSelectedAuthor}
              onSortChange={setSortBy}
              resultsCount={filteredPosts.length}
            />

            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                <span className="ml-3 text-text-secondary">Loading articles...</span>
              </div>
            ) : currentPosts.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
                {currentPosts.map(post => <BlogCard key={post.id} post={post} handleReadMore={setSelectedPostId} />)}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-secondary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="Search" size={24} />
                </div>
                <h3 className="text-lg font-medium">No articles found</h3>
                <button onClick={() => { setSearchQuery(''); setSelectedCategory('all'); setSelectedAuthor('all'); }} className="btn-primary mt-4">Clear Filters</button>
              </div>
            )}

            {totalPages > 1 && (
              <div className="flex items-center justify-center space-x-2">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                  <button key={page} onClick={() => setCurrentPage(page)} className={`px-4 py-2 rounded-md ${currentPage === page ? 'bg-primary text-white' : 'text-text-secondary'}`}>{page}</button>
                ))}
              </div>
            )}
          </div>

          <div className="lg:col-span-4">
            <Sidebar categories={categories} popularPosts={allBlogPosts.sort((a, b) => b.views - a.views).slice(0, 5)} onCategorySelect={setSelectedCategory} />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Blog;
